import React, { useMemo } from 'react';
import styled from 'styled-components';
import _find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import { FilterItem } from './types';
import { Icon } from '../icon/Icon';
import { mainTheme } from '@plecto/ui';

const StyledLabel = styled.div`
	overflow: hidden;
	min-width: 1px;
	font-size: 14px;
	display: flex;
	gap: 6px;
	align-items: center;
`;

const StyledDefaultText = styled.span`
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
	flex: 1 auto;
`;

const StyledSelectionText = styled.span`
	white-space: nowrap;
	flex: 0 auto;
	background: ${mainTheme.fill.infoSecondary};
	color: ${mainTheme.text.primary};
	border-radius: 2px;
	font-size: 12px;
	font-weight: 500;
	min-width: 20px;
	text-align: center;
	display: inline-block;
	// Without this the count sat flush against the pill's edges.
	padding: 0 6px;
	line-height: 18px;
`;

const StyledFilterIcon = styled(Icon)`
	color: ${mainTheme.text.secondary};
	font-size: 14px;
`;

type SelectionStatusProps = {
		active: boolean;
		label: string;
		compactLabel?: boolean;
		selection: string[];
		items: FilterItem[];
		hideExtraLabel?: boolean;
		newLayout?: boolean;
		showIcon?: boolean;
}

export function FilterStatus({
	active,
	selection = [],
	items,
	label,
	compactLabel = false,
	newLayout = false,
	showIcon = true,
}: SelectionStatusProps) {
	const { t } = useTranslation();

	const firstSelected = useMemo(() => {
		return active ? _find(items, (item) => item.id === selection?.[0]) : null;
	}, [active, items, selection]);

	const selectionCount = selection.length - 1;

	const selectionLabel = compactLabel ? t('+ {{count}}', {
		count: selectionCount
	}) : t('+ {{count}} more', {
		count: selectionCount
	});

	const selectionCountNewLayout = selection.length;

	const selectionLabelNewLayout = t('{{count}}', {
		count: selectionCountNewLayout
	});

	// The funnel leads the row in the legacy layout — it says what kind of control this is
	// before the label says which one.
	//
	// The new layout carries no funnel. It used to fall through to one whenever nothing was
	// selected, so the icon appeared on an untouched filter and then vanished as soon as you
	// picked something; the count replaces it, and an untouched filter shows its label alone.
	return <StyledLabel>
		{newLayout
			? active && selection.length > 0 && (
				<StyledSelectionText>{selectionLabelNewLayout}</StyledSelectionText>
			)
			: showIcon && <StyledFilterIcon name="filter" />
		}
		{active && !newLayout ? <>
			<StyledDefaultText>{firstSelected?.label}</StyledDefaultText>
			{selection && selection.length > 1 ? <StyledSelectionText>{selectionLabel}</StyledSelectionText> : null}
		</> : <StyledDefaultText>{label}</StyledDefaultText>}
	</StyledLabel>;
}
